import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';

import {
  getCoursesState,
  TUpdateCourseAsAdmin,
  updateCourseAsAdmin,
} from '../../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../../hooks';
import { checkUserPermission } from '../../../../../../../helper';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import AddCoursePermissionTemplate from '../../../../../Settings/admin/Tabs/CoursePermissionTemplatesTab/AddCoursePermissionTemplate';
import { ICoursePermissionTemplate } from '../../../../../../../store/V2/CoursePermissionTemplatesSlice';
import CoursePermissionTemplateDropdown, {
  TCoursePermissionTemplateOption,
} from '../../../../../../../components/Inputs/admin/CoursePermissionTemplateDropdown';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../components/V2/Buttons';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/V2/RolesSlice';

interface OthersTabProps {
  onClose: () => void;
}

type TState = {
  visibility?: boolean;
  selfEnrolement?: boolean;
  selectedPermissionTemplate: TCoursePermissionTemplateOption | null;
  isChatEnabled: boolean;
  isEveryoneCanChat: boolean;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  visibility: false,
  selfEnrolement: false,
  selectedPermissionTemplate: null,
  isChatEnabled: true,
  isEveryoneCanChat: false,
};

const OthersTab: React.FC<OthersTabProps> = ({ onClose }) => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setLoading] = useState(false);
  const [
    isAddNewPermissionTemplateModalOpen,
    setAddNewPermissionTemplateModalOpen,
  ] = useState(false);

  const courseData = coursesState.adminCourse;

  useEffect(() => {
    if (courseData) {
      const permissionTemplateData = courseData.permissionTemplate as ICoursePermissionTemplate;

      setState((prevState) => {
        const newStateData = {
          ...prevState,
          visibility: courseData.visibility || false,
          selfEnrolement: courseData.selfEnrolement || false,
        };

        if (courseData.chat) {
          newStateData.isChatEnabled = courseData.chat.isChatEnabled;
          newStateData.isEveryoneCanChat = courseData.chat.isEveryoneCanChat;
        }

        if (permissionTemplateData) {
          newStateData.selectedPermissionTemplate = {
            label: permissionTemplateData.name,
            value: permissionTemplateData._id,
          };
        }

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleCheckBoxChange = (name: string, value: boolean) => {
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (courseData) {
      setLoading(true);

      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        visibility: state.visibility,
        selfEnrolement: state.selfEnrolement,
        chat: {
          isChatEnabled: state.isChatEnabled,
          isEveryoneCanChat: state.isEveryoneCanChat,
        },
      };

      if (state.selectedPermissionTemplate) {
        apiData.permissionTemplate = state.selectedPermissionTemplate.value;
      }

      await dispatch(updateCourseAsAdmin(apiData));
    }

    setLoading(false);
  };

  return (
    <div style={{ padding: '50px 30px 30px 30px' }}>
      <div style={{ marginBottom: '10px' }}>
        <Typography variant="h6">Chat</Typography>
      </div>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormGroup sx={{ width: 'fit-content' }}>
              <FormControlLabel
                disabled={isLoading}
                label="Enable Chat"
                control={
                  <Checkbox
                    checked={state.isChatEnabled}
                    onChange={(event, value) =>
                      handleCheckBoxChange('isChatEnabled', value)
                    }
                  />
                }
              />
              {!courseData?.isOneToOne ? (
                <FormControlLabel
                  disabled={isLoading}
                  label="Enable Everyone chat"
                  control={
                    <Checkbox
                      checked={state.isEveryoneCanChat}
                      onChange={(event, value) =>
                        handleCheckBoxChange('isEveryoneCanChat', value)
                      }
                    />
                  }
                />
              ) : null}
            </FormGroup>
          </Grid>
        </Grid>
      </div>
      <div style={{ margin: '10px 0' }}>
        <Typography variant="h6">Visibility</Typography>
      </div>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormGroup sx={{ width: 'fit-content' }}>
              <FormControlLabel
                disabled={isLoading || courseData?.isOneToOne}
                label="Show in landing page"
                control={
                  <Checkbox
                    checked={state.visibility}
                    onChange={(event, value) =>
                      handleCheckBoxChange('visibility', value)
                    }
                  />
                }
              />
              <FormControlLabel
                disabled={isLoading || courseData?.isOneToOne}
                label="Students can enroll themselves"
                control={
                  <Checkbox
                    checked={state.selfEnrolement}
                    onChange={(event, value) =>
                      handleCheckBoxChange('selfEnrolement', value)
                    }
                  />
                }
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <CoursePermissionTemplateDropdown
                selectedItem={state.selectedPermissionTemplate}
                handleChange={(e, value) =>
                  updateStateAttributes({
                    selectedPermissionTemplate: value,
                  })
                }
                componentProps={{
                  fullWidth: true,
                }}
                textFieldProps={{
                  label: 'Permission Template',
                  placeholder: 'Select',
                  ...textFieldProps,
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <DarkButton
              variant="contained"
              color="inherit"
              sx={{ ...defaultButtonStyles }}
              onClick={() => setAddNewPermissionTemplateModalOpen(true)}
            >
              Add New Pricing Template
            </DarkButton>
          </Grid>
        </Grid>
      </div>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt="60px"
        spacing="10px"
      >
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
        {checkUserPermission(authState.user, {
          adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
        }) ? (
          <LoadingButton
            disableElevation
            variant="contained"
            color="primary"
            loading={isLoading}
            onClick={handleSubmit}
          >
            Save
          </LoadingButton>
        ) : null}
      </Stack>
      {/* MODALS */}
      {isAddNewPermissionTemplateModalOpen ? (
        <AddCoursePermissionTemplate
          open={isAddNewPermissionTemplateModalOpen}
          onClose={() => setAddNewPermissionTemplateModalOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default OthersTab;
